import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { pick, pathOr } from 'ramda';
import { SERVICE_VIEWS } from './ServiceConstants';
import ConfigurationList from './ConfigurationList';
import ServiceDetails from './ServiceDetails';
import FeatureList from './FeatureList';
import Table from '../Table';
import ValueSources from '../../sources/value';
import { isNullOrEmpty } from '../../utils/core';
import { addNotification } from '../../actions/reduxActions/notification';

/**
 * @param {Object} configurationListItem Describes a single configuration value
 * @param {Function} displayError Dispatches an error notification
 * @param {String} authenticationToken
 *
 * @description When displaying configuration values, the value needs
 * to display the name, which isn't provided. So for every config
 * value we must ask the api for additional value info.
 */
export const mapConfigToListItem = async (configurationListItem, displayError, authenticationToken) => {

    try {
        const value = await ValueSources.get(configurationListItem.valueElementToReferenceId.id, authenticationToken);

        return {
            name: configurationListItem.developerName,
            valueName: value ? value.developerName : null,
            valueType: configurationListItem.contentType.replace('Content', ''), // remove "Content" from the type
            valueId: configurationListItem.valueElementToReferenceId.id,

            // this starts off as null, but when it is joined with the values from a Service, it is set to the correct value
            isInCurrentService: null,
        };

    } catch (error) {
        displayError({
            type: 'error',
            message: error.message,
            isPersistent: true,
        });

        return configurationListItem;
    }
};

export const ServiceEditor = ({
    authenticationToken,
    tenantId,
    switchView,
    switchToRefreshServiceConfig,
    saveService,
    currentServiceData = null,
    newServiceData = null,
    addNotification: displayError,
}) => {

    // If we have been given a current service this is an edit, otherwise if null it's a create
    const doesServiceAlreadyExist = isNullOrEmpty(currentServiceData) === false;

    const [name, setName] = useState(currentServiceData ? currentServiceData.developerName : '');
    const [url, setUrl] = useState(currentServiceData ? currentServiceData.uri : '');
    const [username, setUsername] = useState(currentServiceData ? currentServiceData.httpAuthenticationUsername : '');
    const [password, setPassword] = useState('');
    const [comments, setComments] = useState(currentServiceData ? currentServiceData.developerSummary : '');
    const [configurationListItems, setConfigurationListItems] = useState([]);
    const [configItemsLoading, setConfigItemsLoading] = useState(true);

    const onDetailsChange = ({ property, value }) => {
        switch (property) {
        case 'name': return setName(value);
        case 'url': return setUrl(value);
        case 'username': return setUsername(value);
        case 'password': return setPassword(value);
        case 'comments': return setComments(value);
        default: return null;
        }
    };

    let featuresSection;
    let typesAndActionsSection;
    let primaryButton;
    let configurationList;

    if (doesServiceAlreadyExist) {

        const serviceFeatures = pick([
            'providesAutoBinding',
            'providesDatabase',
            'providesFiles',
            'providesIdentity',
            'providesLocation',
            'providesLogic',
            'providesSocial',
            'providesViews',
        ], currentServiceData);

        featuresSection = (
            <React.Fragment>
                <h2>Features</h2>
                <FeatureList serviceFeatures={serviceFeatures} />
            </React.Fragment>
        );
        typesAndActionsSection = (
            <div className="flex-grow">
                <h2>
                    {'Existing Actions'}
                    <span className="margin-left">
                        {`Total: ${pathOr(0, ['actions', 'length'], currentServiceData)}`}
                    </span>
                </h2>
                <Table
                    className="table margin-top table-striped"
                    columns={
                        [
                            {
                                renderHeader: () => 'Name',
                                renderCell: ({ item }) => item.developerName,
                            },
                            {
                                renderHeader: () => 'Uri',
                                renderCell: ({ item }) => item.uriPart,
                            },
                        ]
                    }
                    items={pathOr([], ['actions'], currentServiceData)}
                />

                <h2>
                    {'Existing Types'}
                    <span className="margin-left">
                        {`Total: ${pathOr(0, ['install', 'typeElements', 'length'], currentServiceData)}`}
                    </span>
                </h2>
                <Table
                    className="table margin-top table-striped"
                    columns={
                        [
                            {
                                renderHeader: () => 'Name',
                                renderCell: ({ item }) => item.developerName,
                            },
                        ]
                    }
                    items={pathOr([], ['install', 'typeElements'], currentServiceData)}
                />
            </div>
        );
        primaryButton = (
            <React.Fragment>
                <button
                    type="button"
                    className="btn btn-primary outcome"
                    onClick={() => saveService(name, url, username, password, comments)}
                >
                    {'Save Service'}
                </button>
                <button
                    type="button"
                    className="btn btn-success outcome"
                    onClick={() => switchToRefreshServiceConfig(name, url, username, password, comments, currentServiceData.id)}
                >
                    {'Refresh Service Configuration Data'}
                </button>
            </React.Fragment>
        );

        // initially set loading while fetching each value individually
        configurationList = (
            <ConfigurationList
                configurationValues={configurationListItems}
                isLoading={configItemsLoading}
                url={currentServiceData.uri}
                username={username}
                password={password}
                authenticationToken={authenticationToken}
                tenantId={tenantId}
            />
        );

        const mapToListItems = async (configurationValues = []) => Promise.all(configurationValues.map(value => mapConfigToListItem(
            value,
            displayError,
            authenticationToken,
        )));

        // only happens on first render
        useEffect(() => {
            async function fetchData() {
                // need to fire individual api calls for each value to get their name for the list
                mapToListItems(currentServiceData.configurationValues || []).then((listItems) => {
                    setConfigurationListItems(listItems);
                    setConfigItemsLoading(false);
                });
            }
            fetchData();
        }, []);

    } else {
        primaryButton = (
            <React.Fragment>
                <button
                    type="button"
                    className="btn btn-primary outcome"
                    onClick={() => switchToRefreshServiceConfig(name, url, username, password, comments)}
                >
                    {'Retrieve Service Configuration Data'}
                </button>
            </React.Fragment>
        );
    }

    return (
        <div className="full-height flex-column">
            <div className="admin-page main-scroller">
                <h1>{ doesServiceAlreadyExist ? `Service: ${currentServiceData.developerName}` : 'New Service' }</h1>
                <ServiceDetails service={doesServiceAlreadyExist ? currentServiceData : newServiceData} onChange={onDetailsChange} />
                {featuresSection}
                {configurationList}
                {typesAndActionsSection}
            </div>
            <div className="mw-footer container-fluid">
                <button
                    type="button"
                    className="btn btn-default outcome"
                    onClick={() => switchView(SERVICE_VIEWS.ServiceList)}
                >
                    {'Back'}
                </button>
                {primaryButton}
            </div>
        </div>
    );
};

const mapDispatchToProps = {
    addNotification,
};

export default connect(null, mapDispatchToProps)(ServiceEditor);
